import { IconInfoCircle } from "@tabler/icons-react";
import { useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { z } from "zod";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import ContentHeader from "../../components/app/headers/ContentHeader";
import KeyboardAvoidingView from "../../components/layout/KeyboardAvoidingView";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useApiQuery } from "../../services/api";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { defaultTheme } from "../../domains/app/contexts/theme.context";
import { getRoomName } from "../../domains/room/hooks/useRoomName";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import {
  gaEventMeshSuggestionClicked,
  gaEventSearchTabSelected,
  type SearchSource,
} from "../../tools/analytics/domains/search.analytics";
import { SearchCategoryTabs } from "./components/SearchCategoryTabs";
import { SearchHistoryList } from "./components/SearchHistoryList";
import { SearchInput } from "./components/SearchInput";
import { SearchResults } from "./components/SearchResults";
import { SearchRoomScopeToggle } from "./components/SearchRoomScopeToggle";
import { SearchSuggestions } from "./components/SearchSuggestions";
import { useSearchHistory } from "./hooks/useSearchHistory";
import { useRecordSettledSearch } from "./hooks/useRecordSettledSearch";
import { useSearchScope } from "./hooks/useSearchScope";
import { useSearchValue } from "./hooks/useSearchValue";
import {
  useTrackSearchQuery,
  type TrackSearchQuery,
} from "./hooks/useTrackSearchQuery";
import { SearchCategorySchema, type SearchCategory } from "./types";

// Minimum input length before search is activated. Single- and two-letter
// queries match an enormous portion of the corpus on trigram (every article
// title with the substring), driving the worst-case search latency. 3 chars
// also matches pg_trgm's default word-similarity granularity. Below the
// threshold the page renders the history/suggestion view.
export const SEARCH_MIN_LENGTH = 3;

// Set by the desktop search palette's "See all": it already recorded this term
// and reported its article count, so this page does not count it twice.
const searchLocationStateSchema = z.object({
  paletteTrackedTerm: z.string().nullable().default(null),
});

export function SearchPage({
  roomId,
}: {
  roomId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const searchValue = useSearchValue();
  const scope = useSearchScope(roomId);
  const history_ = useSearchHistory();
  const { currentUser } = useCurrentUser();
  const category = parseCategory(location.search);
  const tracking = useTrackSearchQuery();
  const { paletteTrackedTerm } = useLocationState(searchLocationStateSchema);

  const settledSearchValue = useRecordSettledSearch(
    searchValue.length >= SEARCH_MIN_LENGTH ? searchValue : "",
    history_.addEntry
  );

  const gatedFireResultsSettled = useCallback(
    (params: Parameters<TrackSearchQuery["fireResultsSettled"]>[0]) => {
      if (params.term !== settledSearchValue) return;
      if (params.term === paletteTrackedTerm && params.category === "articles")
        return;
      tracking.fireResultsSettled(params);
    },
    [tracking.fireResultsSettled, settledSearchValue, paletteTrackedTerm]
  );

  // Topics chantier: search suggestions prefer the user's selected JuisciTopics
  // (resolved names), falling back to their specialty mesh terms.
  const myTopicsQuery = useApiQuery(
    "/webapp/private/users/me/topics",
    currentUser === null ? false : {}
  );
  const topicNames =
    myTopicsQuery.state === "success"
      ? myTopicsQuery.body.map((topic) => getItemTranslation(topic))
      : [];
  const suggestions = buildSuggestions(currentUser, topicNames);

  const roomIdForQuery = scope.kind === "room" ? scope.roomId : null;
  const scopeKind = scope.kind;

  const roomQuery = useApiQuery(
    "/webapp/public/rooms/:roomId",
    roomId === null ? false : { param: { roomId }, query: {} }
  );
  // CONTENT language. This component serves TWO routes: `/search` (app page, mounted
  // with `roomId={null}`) and `ROOM_ROUTES.search` = `/room/search/:roomId`. The room
  // name is rendered ONLY when `roomId !== null` — the scope chip and the "Showing
  // results from <room> only" line — so every render of it is on the IN-ROOM route,
  // naming the room the reader is inside. Same rule as the room header.
  //
  // On `/search` the name is never rendered and the room query is disabled, so the
  // axis is unobservable there.
  const { roomTextLang } = useActiveLang();
  const searchRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const roomName =
    searchRoom === null ? "" : getRoomName(searchRoom, roomTextLang);

  const replaceParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(location.search);
      mutate(params);
      const qs = params.toString();
      history.replace(`${location.pathname}${qs.length === 0 ? "" : `?${qs}`}`);
    },
    [history, location.pathname, location.search]
  );

  const handleSearch = useCallback(
    (next: string, source: SearchSource) => {
      const trimmed = next.trim();
      replaceParams((params) => {
        if (trimmed.length === 0) {
          params.delete("value");
        } else {
          params.set("value", trimmed);
        }
      });
      if (trimmed.length > 0) {
        if (source !== "typed") {
          history_.addEntry(trimmed);
        }
        tracking.recordIntent(trimmed, source);
      }
    },
    [history_, replaceParams, tracking]
  );

  const handleSelectHistory = useCallback(
    (entry: string) => {
      handleSearch(entry, "history");
    },
    [handleSearch]
  );

  const handleSelectSuggestion = useCallback(
    (entry: string, isPrimarySpecialty: boolean) => {
      gaEventMeshSuggestionClicked({
        term: entry,
        isPrimarySpecialty,
      });
      handleSearch(entry, "suggestion");
    },
    [handleSearch]
  );

  const handleSelectAiSuggestion = useCallback(
    (entry: string) => {
      handleSearch(entry, "suggestion");
    },
    [handleSearch]
  );

  const handleScopeToggle = useCallback(
    (nextScopedToRoom: boolean) => {
      replaceParams((params) => {
        if (nextScopedToRoom) {
          params.delete("scope");
        } else {
          params.set("scope", "global");
        }
      });
    },
    [replaceParams]
  );

  const handleCategoryChange = useCallback(
    (next: SearchCategory) => {
      gaEventSearchTabSelected({
        from: category,
        to: next,
        term: searchValue,
      });
      replaceParams((params) => {
        if (next === "all") {
          params.delete("category");
        } else {
          params.set("category", next);
        }
      });
    },
    [category, searchValue, replaceParams]
  );

  return (
    <ScrollableLayout
      background={defaultTheme.colors.background}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent />}
      footer={null}
    >
      <KeyboardAvoidingView behavior="padding">
        <div className="flex w-full flex-col gap-6 px-5 pt-3 pb-8">
          <SearchInput
            value={searchValue}
            placeholder={t("Search articles, rooms, topics…")}
            autoFocus
            onSearch={handleSearch}
          />
          {roomId !== null && roomName.length > 0 && (
            <SearchRoomScopeToggle
              roomName={roomName}
              isScopedToRoom={scope.kind === "room"}
              onToggle={handleScopeToggle}
            />
          )}
          {searchValue.length >= SEARCH_MIN_LENGTH && (
            <SearchCategoryTabs
              active={category}
              onChange={handleCategoryChange}
            />
          )}
          {searchValue.length >= SEARCH_MIN_LENGTH &&
            scope.kind === "room" &&
            roomName.length > 0 && (
              <div className="flex w-full items-center gap-2 rounded-xl bg-ds-slate-100 p-3 text-xs text-ds-slate-700">
                <IconInfoCircle size={16} className="shrink-0" />
                <p>
                  <Trans
                    i18nKey="Showing results from <strong>{{roomName}}</strong> only"
                    values={{ roomName }}
                    components={{
                      strong: <span className="font-semibold" />,
                    }}
                  />
                </p>
              </div>
            )}
          {searchValue.length >= SEARCH_MIN_LENGTH ? (
            <>
              <SearchSuggestions
                searchValue={searchValue}
                onSelectRefine={handleSelectAiSuggestion}
              />
              <SearchResults
                searchValue={searchValue}
                roomIdForQuery={roomIdForQuery}
                category={category}
                scope={scopeKind}
                fireResultsSettled={gatedFireResultsSettled}
              />
            </>
          ) : (
            <SearchHistoryList
              history={history_.history}
              suggestions={suggestions.items}
              firstSuggestionIsPrimary={suggestions.firstIsPrimary}
              onSelectHistory={handleSelectHistory}
              onSelectSuggestion={handleSelectSuggestion}
              onRemove={history_.removeEntry}
            />
          )}
        </div>
      </KeyboardAvoidingView>
    </ScrollableLayout>
  );
}

function parseCategory(search: string): SearchCategory {
  const raw = new URLSearchParams(search).get("category");
  const result = SearchCategorySchema.safeParse(raw);
  return result.success ? result.data : "all";
}

function buildSuggestions(
  currentUser: ReturnType<typeof useCurrentUser>["currentUser"],
  topicNames: string[]
): { items: string[]; firstIsPrimary: boolean } {
  const MAX = 5;
  if (currentUser === null) return { items: [], firstIsPrimary: false };

  if (topicNames.length > 0) {
    return { items: topicNames.slice(0, MAX), firstIsPrimary: true };
  }

  const { primary, other } = currentUser.specialtyMeshTerms;
  const items = [...primary, ...other].slice(0, MAX);
  return { items, firstIsPrimary: primary.length > 0 };
}
